import React, { useEffect, useState } from "react";
import { Flame, TrendingUp, Crown, ArrowRight, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getStoredUser, clearStoredUser } from "./RegisterModal";
import { getCoachStreak, getUsage, logout, clearSessionToken } from "../lib/api";
import { useI18n } from "../i18n";

const SCORE_KEY = "goldora_coach_score";
const PLAN_LABEL = {
  free:  { name: "Free",  color: "text-neutral-400",  border: "border-white/10",      bg: "bg-white/5" },
  pro:   { name: "Pro",   color: "text-gold-300",     border: "border-gold-500/40",   bg: "bg-gold-500/10" },
  elite: { name: "Elite", color: "text-gold-200",     border: "border-gold-400",      bg: "bg-gold-500/15" },
};

// Compact user status card for the mobile drawer header.
// Shows avatar + name + plan, plus Coach score + streak when available.
export default function UserStatusCard({ onNavigate }) {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const user = getStoredUser();
  const [streak, setStreak] = useState(null);
  const [plan, setPlan] = useState("free");
  const [score, setScore] = useState(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    onNavigate?.();
    await logout();
    clearSessionToken();
    clearStoredUser();
    navigate("/login");
  };

  useEffect(() => {
    // Local score (fast)
    try {
      const raw = localStorage.getItem(SCORE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.score != null) setScore(parsed);
      }
    } catch {/* noop */}
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    let alive = true;
    getCoachStreak(user.email, lang).then((d) => { if (alive) setStreak(d); }).catch(() => {});
    getUsage().then((u) => { if (alive && u?.plan) setPlan(u.plan); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.email, lang]);

  if (!user?.email) {
    // Not logged in → CTA
    return (
      <Link
        to="/planos"
        onClick={onNavigate}
        data-testid="drawer-cta-signup"
        className="block mx-3 mt-3 mb-2 rounded-lg border border-gold-500/40 bg-gold-500/[0.08] px-4 py-3.5 group"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-300">
              {pt ? "Comece grátis" : "Start free"}
            </div>
            <div className="mt-0.5 font-display text-base text-white">
              {pt ? "Crie sua conta GOLDORA" : "Create your GOLDORA account"}
            </div>
          </div>
          <ArrowRight size={16} className="text-gold-300 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Link>
    );
  }

  const planMeta = PLAN_LABEL[plan] || PLAN_LABEL.free;
  const initial = (user.name || user.email).trim().charAt(0).toUpperCase();
  const days = streak?.current_streak || 0;
  const flameOn = days >= 1;
  const flameColor = days >= 30 ? "#E9B934" : days >= 7 ? "#f97316" : "#fbbf24";

  return (
    <div className="mx-3 mt-3 mb-2 rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="drawer-user-card">
      {/* top row: avatar + name + plan */}
      <div className="flex items-center gap-3 px-4 py-3.5">
        <div className="w-11 h-11 rounded-full grid place-items-center bg-gradient-to-br from-gold-500/30 to-gold-500/5 border border-gold-500/40 text-gold-200 font-display text-lg shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 shrink-0">
              {pt ? "Olá," : "Hi,"}
            </div>
            <div
              className={`text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded border ${planMeta.border} ${planMeta.bg} ${planMeta.color} flex items-center gap-1`}
              data-testid="drawer-plan-badge"
            >
              {plan !== "free" && <Crown size={9} />}
              {planMeta.name}
            </div>
          </div>
          <div className="mt-0.5 font-display text-base text-white truncate" data-testid="drawer-user-name">
            {user.name || user.email}
          </div>
        </div>
      </div>

      {/* bottom row: score + streak */}
      {(score || streak) && (
        <div className="grid grid-cols-2 border-t border-white/5">
          <Link
            to="/coach"
            onClick={onNavigate}
            data-testid="drawer-stat-score"
            className="flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.04] transition-colors border-r border-white/5"
          >
            <TrendingUp size={14} className="text-gold-400 shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-neutral-500 truncate">
                {pt ? "Score Coach" : "Coach Score"}
              </div>
              <div className="font-mono text-sm text-white">
                {score ? <>{score.score}<span className="text-neutral-500 text-[10px]"> / 100</span></> : <span className="text-neutral-500 text-xs">—</span>}
              </div>
            </div>
          </Link>
          <Link
            to="/coach"
            onClick={onNavigate}
            data-testid="drawer-stat-streak"
            className="flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.04] transition-colors"
          >
            <Flame
              size={14}
              style={{ color: flameOn ? flameColor : "#525252" }}
              className={flameOn ? "flame-pulse shrink-0" : "shrink-0"}
            />
            <div className="min-w-0">
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-neutral-500 truncate">
                {pt ? "Sequência" : "Streak"}
              </div>
              <div className="font-mono text-sm text-white">
                {days}<span className="text-neutral-500 text-[10px]"> {pt ? (days === 1 ? "dia" : "dias") : (days === 1 ? "day" : "days")}</span>
              </div>
            </div>
          </Link>
        </div>
      )}

      <button
        onClick={handleLogout}
        data-testid="drawer-logout-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-red-300 hover:bg-red-500/[0.04] border-t border-white/5 transition-colors"
      >
        <LogOut size={12} />
        {pt ? "Sair" : "Sign out"}
      </button>
    </div>
  );
}
